import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import ProblemRecordEditor from '../components/ProblemRecordEditor.jsx';
import RiskRecordEditor from '../components/RiskRecordEditor.jsx';
import ScheduleRecordEditor from '../components/ScheduleRecordEditor.jsx';
import {
  getProblems, getProgressSchedules, getRisks,
} from '../api/dashboardApi.js';
import { DASHBOARD_TABS } from '../constants/tabs.js';
import {
  LARGE_CATEGORY_ORDER,
  toDate,
  normalizeImpactLevel,
  countByImpactLevel,
  groupDetailsByImpact,
  isRowCompleted,
  makeTaskDetail,
  calcTaskData,
  buildRegionMatrix,
} from '../utils/projectRiskConverters.js';
import '../styles/dashboard-project-risk.css';
import '../styles/dashboard-region-risk.css';

const TASK_LEVELS = [
  { key: 'total', label: '総数', color: '#3b82f6' },
  { key: 'planned', label: '予定', color: '#22c55e' },
  { key: 'completed', label: '実績', color: '#f59e0b' },
  { key: 'delayed', label: '遅れ', color: '#ef4444' },
];

const IMPACT_LEVELS = [
  { key: 'H', label: 'H', color: '#ef4444' },
  { key: 'M', label: 'M', color: '#f59e0b' },
  { key: 'L', label: 'L', color: '#fbbf24' },
];


// ================================================================
// サブコンポーネント
// ================================================================

function ProcessProgressBars({ taskCounts, onBarClick }) {
  const maxValue = Math.max(1, ...TASK_LEVELS.map((l) => taskCounts[l.key] || 0));

  return (
    <div className="prisk-progress-bars">
      {TASK_LEVELS.map((level) => {
        const value = taskCounts[level.key] || 0;
        const width = value > 0 ? Math.max(6, (value / maxValue) * 100) : 0;
        const clickable = value > 0 && onBarClick;
        return (
          <div
            className={`prisk-progress-row ${clickable ? 'prisk-progress-row--clickable' : ''}`}
            key={level.key}
            onClick={clickable ? () => onBarClick(level.key) : undefined}
          >
            <span className="prisk-progress-label">{level.label}</span>
            <div className="prisk-progress-track">
              <div
                className="prisk-progress-fill"
                style={{ width: `${width}%`, backgroundColor: level.color }}
              />
            </div>
            <span className="prisk-progress-count" style={{ color: level.color }}>{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function ImpactSection({ title, total, counts, markerTone, onRowClick }) {
  const maxValue = Math.max(1, ...IMPACT_LEVELS.map((l) => counts[l.key] || 0));

  return (
    <div className="prisk-impact-section">
      <div className="prisk-impact-header">
        <span className={`prisk-impact-marker prisk-impact-marker--${markerTone}`}>&#9888;</span>
        <span className="prisk-impact-title">{title}: {total}</span>
      </div>
      <div className="prisk-impact-rows">
        {IMPACT_LEVELS.map((level) => {
          const value = counts[level.key] || 0;
          const width = value > 0 ? Math.max(6, (value / maxValue) * 100) : 0;
          const clickable = value > 0;
          return (
            <div
              className={`prisk-impact-row ${clickable ? 'prisk-impact-row--clickable' : ''}`}
              key={level.key}
              onClick={clickable ? () => onRowClick(level.key) : undefined}
            >
              <span className="prisk-impact-level">{level.label}</span>
              <div className="prisk-impact-track">
                <div
                  className="prisk-impact-fill"
                  style={{ width: `${width}%`, backgroundColor: level.color }}
                />
              </div>
              <span className="prisk-impact-count">{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IssueRiskBars({ issueCounts, riskCounts, issueDetails, riskDetails, label, onOpenModal }) {
  const issueTotal = issueCounts.H + issueCounts.M + issueCounts.L;
  const riskTotal = riskCounts.H + riskCounts.M + riskCounts.L;

  return (
    <div className="prisk-issue-risk-bars">
      <ImpactSection
        title="課題"
        total={issueTotal}
        counts={issueCounts}
        markerTone="critical"
        onRowClick={(level) =>
          onOpenModal(`${label} - 課題 (${level})`, /* istanbul ignore next -- 件数0の階層は非クリック */ (issueDetails[level] || []))
        }
      />
      <ImpactSection
        title="リスク"
        total={riskTotal}
        counts={riskCounts}
        markerTone="warning"
        onRowClick={(level) =>
          onOpenModal(`${label} - リスク (${level})`, /* istanbul ignore next -- 件数0の階層は非クリック */ (riskDetails[level] || []))
        }
      />
    </div>
  );
}

function DetailModal({ visible, onClose, title, items, onItemClick }) {
  if (!visible) return null;

  return (
    <div className="rrisk-modal-overlay" onClick={onClose}>
      <div className="rrisk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rrisk-modal__header">
          <h3 className="rrisk-modal__title">{title}</h3>
          <button className="rrisk-modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </div>
        <div className="rrisk-modal__body">
          {/* istanbul ignore next -- モーダルは件数のある行からしか開かない */ items.length === 0 ? (
            <p className="rrisk-modal__empty">データがありません</p>
          ) : (
            <ul className="rrisk-modal__list">
              {items.map((item, idx) => {
                const hasDetailPopup = item.rawRow && onItemClick;
                return (
                  <li className="rrisk-modal__item" key={idx}>
                    <div className="rrisk-modal__item-main">
                      {hasDetailPopup ? (
                        <button
                          className="rrisk-modal__link-btn"
                          onClick={() => onItemClick(item)}
                          type="button"
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span className="rrisk-modal__text">{item.title}</span>
                      )}
                      {item.owner && (
                        <span className="rrisk-modal__owner">{item.owner}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// メインコンポーネント
// ================================================================

export default function DashboardProjectRiskPage() {
  const [matrixData, setMatrixData] = useState({ columns: [], regionData: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalItems, setModalItems] = useState([]);

  const [editingRow, setEditingRow] = useState(null);
  const [editingProblem, setEditingProblem] = useState(null);
  const [editingRisk, setEditingRisk] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [scheduleData, problemData, riskData] = await Promise.all([
        getProgressSchedules(),
        getProblems(),
        getRisks(),
      ]);
      setMatrixData(buildRegionMatrix(scheduleData, problemData, riskData));
    } catch {
      setError('課題・リスク（プロジェクト別）データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadData().finally(() => { if (!active) return; });
    return () => { active = false; };
  }, [loadData]);

  const openModal = useCallback((title, items) => {
    setModalTitle(title);
    setModalItems(items);
    setModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setModalItems([]);
  }, []);

  // モーダルのitemsは開いた時点のスナップショットのため、保存・削除後は閉じて再取得し、
  // 古いupdated_at_isoでの再編集（偽409）や削除済みレコードの再表示を防ぐ
  const handleEditorSaved = useCallback(() => {
    closeModal();
    loadData();
  }, [closeModal, loadData]);

  const openItemDetail = useCallback((item) => {
    /* istanbul ignore next -- 呼び出し元は item.rawRow を持つ項目のみ渡すため item への防御フォールバック */
    const row = item.rawRow || item;
    const type = item.type;
    if (type === 'problem') {
      setEditingProblem({ ...row });
    } else if (type === 'risk') {
      setEditingRisk({ ...row });
    } else {
      setEditingRow({ ...row });
    }
  }, []);

  const gridTemplateColumns = useMemo(
    () => ['140px', ...matrixData.columns.map(() => '240px')].join(' '),
    [matrixData.columns],
  );

  const TASK_LABEL_MAP = useMemo(() => ({
    total: '総数タスク',
    planned: '予定タスク',
    completed: '実績タスク',
    delayed: '遅れタスク',
  }), []);

  return (
    <DashboardPageFrame
      mainClassName="prisk-main"
      rootClassName="prisk-page"
      secondaryTabActiveIndex={4}
      secondaryTabs={DASHBOARD_TABS}
    >
      <section className="prisk-panel">
        <div className="prisk-table-wrapper">
          <div className="prisk-table">
            {/* ヘッダー行 */}
            <div className="prisk-row prisk-row--head" style={{ gridTemplateColumns }}>
              <div className="prisk-cell prisk-cell--sticky">項目</div>
              {matrixData.columns.map((col) => (
                <div className="prisk-cell" key={col}>
                  {col}
                </div>
              ))}
            </div>

            {loading && (
              <div className="prisk-message">読み込み中...</div>
            )}

            {!loading && error && (
              <div className="prisk-message prisk-message--error">{error}</div>
            )}

            {!loading && !error && matrixData.regionData.length === 0 && (
              <div className="prisk-message">データがありません</div>
            )}

            {!loading && !error && matrixData.regionData.map((region) => (
              <div className="prisk-region-group" key={region.oemRegion}>
                {/* OEM×対地 グループヘッダー */}
                <div className="prisk-row prisk-row--region-header" style={{ gridTemplateColumns }}>
                  <div className="prisk-cell prisk-cell--sticky prisk-cell--region-name">
                    {region.oemRegion}
                  </div>
                  {matrixData.columns.map((col) => (
                    <div className="prisk-cell prisk-cell--region-fill" key={`header-${region.oemRegion}-${col}`} />
                  ))}
                </div>

                {/* 進捗状況 行 */}
                <div className="prisk-row" style={{ gridTemplateColumns }}>
                  <div className="prisk-cell prisk-cell--sticky prisk-cell--label">進捗状況</div>
                  {region.processColumns.map((col) => (
                    <div className="prisk-cell" key={`progress-${region.oemRegion}-${col.processName}`}>
                      <ProcessProgressBars
                        taskCounts={col.taskCounts}
                        onBarClick={(levelKey) =>
                          openModal(
                            `${region.oemRegion} > ${col.processName} - ${TASK_LABEL_MAP[levelKey]}`,
                            /* istanbul ignore next -- 件数0のバーは非クリック */ (col.taskDetails[levelKey] || []),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>

                {/* 課題・リスク 行 */}
                <div className="prisk-row" style={{ gridTemplateColumns }}>
                  <div className="prisk-cell prisk-cell--sticky prisk-cell--label">課題・リスク</div>
                  {region.processColumns.map((col) => (
                    <div className="prisk-cell" key={`issuerisk-${region.oemRegion}-${col.processName}`}>
                      <IssueRiskBars
                        issueCounts={col.issueCounts}
                        riskCounts={col.riskCounts}
                        issueDetails={col.issueDetails}
                        riskDetails={col.riskDetails}
                        label={`${region.oemRegion} > ${col.processName}`}
                        onOpenModal={openModal}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DetailModal
        items={modalItems}
        title={modalTitle}
        visible={modalVisible}
        onClose={closeModal}
        onItemClick={openItemDetail}
      />

      <ScheduleRecordEditor
        initialRow={editingRow}
        onClose={() => setEditingRow(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingRow)}
      />

      <ProblemRecordEditor
        initialRow={editingProblem}
        onClose={() => setEditingProblem(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingProblem)}
      />

      <RiskRecordEditor
        initialRow={editingRisk}
        onClose={() => setEditingRisk(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingRisk)}
      />
    </DashboardPageFrame>
  );
}
